import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type { UserI } from '@/pages/Status/Login/interface/UserInterface.interface';
import { CheckValidApi } from '@/routes/api/main-api';
import { useUserStore } from '@/store';
import {
  markPerformance,
  measurePerformance,
} from '@/utils/performanceMonitor';
import { getUserDataAsync, hasUserData } from '@/utils/UserDataStore';

// Mark initial loading for performance tracking
markPerformance('auth-context-init');

/**
 * Authentication context interface
 */
interface AuthContextType {
  /** The current authenticated user or null */
  user: UserI | null;
  /** Whether a user is authenticated */
  isAuth: boolean;
  /** Whether authentication is in progress */
  isLoading: boolean;
  /** Whether we're checking authentication status */
  isCheckingAuth: boolean;
}

/**
 * Static context value for login page (optimized path)
 */
const LOGIN_PAGE_CONTEXT: AuthContextType = {
  user: null,
  isAuth: false,
  isLoading: false,
  isCheckingAuth: false,
};

/**
 * Static context value for initial loading state
 */
const INITIAL_LOADING_CONTEXT: AuthContextType = {
  user: null,
  isAuth: false,
  isLoading: true,
  isCheckingAuth: true,
};

// Create the context with undefined default
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Global state to prevent multiple simultaneous decryption operations
let isGlobalDecryptionInProgress = false;

// Fast pre-check - is this request likely for the login page?
const isLikelyLoginPage = () => {
  if (typeof window !== 'undefined') {
    return window.location.pathname.includes('login');
  }
  return false;
};

/**
 * AuthProvider component that manages user authentication state
 *
 * Optimizations:
 * - Fast path for login page (skips decryption)
 * - Async decryption to avoid blocking main thread
 * - Minimizes state updates and re-renders
 * - Pre-initialized render for speed
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  markPerformance('auth-provider-render-start');

  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useUserStore();

  // Local state
  const [initialized, setInitialized] = useState(() => {
    // Fast initial render - initialize immediately for login page
    return isLikelyLoginPage();
  });
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [cachedUserData, setCachedUserData] = useState<UserI | null>(null);

  // Determine if we're on the login page for fast path
  const isLoginPage = location.pathname === '/login';
  
  // Determine if we're on password reset pages
  const isPasswordResetPage = 
    location.pathname === '/forgot-password' || 
    location.pathname === '/reset-password';

  // Check if user data exists without decryption
  const hasStoredData = useMemo(() => hasUserData(), []);

  // Log performance metrics for login page path
  useEffect(() => {
    if (isLoginPage) {
      markPerformance('auth-login-context-provided');
      measurePerformance(
        'auth-context-init',
        'auth-login-context-provided',
        'auth-login-path',
      );
    }
  }, [isLoginPage]);

  // Load and decrypt user data asynchronously
  useEffect(() => {
    // Skip if any condition is met:
    // - Already initialized
    // - No stored data available
    // - Already have user in Zustand store
    // - Decryption already in progress
    // - On login page or password reset pages (fast path)
    if (
      initialized ||
      !hasStoredData ||
      user ||
      isGlobalDecryptionInProgress ||
      isLoginPage ||
      isPasswordResetPage
    ) {
      return;
    }

    // Update loading states
    setIsDecrypting(true);
    isGlobalDecryptionInProgress = true;

    // Async load to avoid blocking the main thread
    getUserDataAsync()
      .then((userData) => {
        if (userData) {
          // Store user data in both local state and Zustand
          setCachedUserData(userData);
          setUser(userData);
        }
      })
      .catch((error) => {
        console.error('Failed to decrypt user data:', error);
      })
      .finally(() => {
        // Clear loading states
        setIsDecrypting(false);
        isGlobalDecryptionInProgress = false;
        setInitialized(true);
      });
  }, [setUser, initialized, user, hasStoredData, isLoginPage, isPasswordResetPage]);

  // Initialize immediately when we don't need to decrypt anything
  useEffect(() => {
    if (!hasStoredData || user || isLoginPage || isPasswordResetPage) {
      setInitialized(true);
    }
  }, [hasStoredData, user, isLoginPage, isPasswordResetPage]);

  // Validate authentication token with API
  const { isLoading, status, error, data } = useQuery({
    queryKey: ['checkValidity'],
    queryFn: CheckValidApi,
    // Run the query if:
    // 1. Not on login page or password reset pages
    // 2. Either initialized OR has stored data (to check auth on refresh)
    // 3. Either has user OR has cached data OR has stored data (cookies might be valid)
    enabled:
      !isLoginPage &&
      !isPasswordResetPage &&
      (initialized || hasStoredData) &&
      (!!user || !!cachedUserData || hasStoredData),
    retry: (failureCount, error) => {
      // Allow retries for network errors or when interceptor is handling token refresh
      if (failureCount < 3) {
        // Increased retry count for better reliability
        // Check if this is a network error or 401 that interceptor can handle
        const axiosError = error as any;
        if (!axiosError?.response || axiosError?.response?.status === 401) {
          return true;
        }
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff with longer max delay
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  // Handle failed authentication
  useEffect(() => {
    // Only process results when we have data or a final error state
    if (!isLoginPage && !isPasswordResetPage && initialized && (data || error)) {

      // Handle successful authentication
      if (data?.success && data?.data) {
        if (!user) {
          setUser(data.data);
        }
        return;
      }

      // Handle authentication failure
      if (data?.success === false && data?.authError) {
        navigate('/login', { replace: true });
        return;
      }

      // Handle connection errors - don't redirect, let retry handle it
      if (data?.connectionError || (!data && error)) {
        return;
      }

      // If we have data but no success and no explicit auth error,
      // it might be a temporary issue - let React Query retry
      if (data && data.success === false && !data.authError) {
        // Don't redirect for non-auth errors
        return;
      }
    }
  }, [status, data, error, navigate, isLoginPage, isPasswordResetPage, initialized, user, setUser]);

  // Get current user data, preferring Zustand store over local cache
  const userData = user || cachedUserData;

  // Create optimized context value based on current state
  const contextValue = useMemo(() => {
    // Optimized path for login page and password reset pages - use static context
    if (isLoginPage || isPasswordResetPage) {
      return LOGIN_PAGE_CONTEXT;
    }

    // Still initializing OR actively checking auth - use loading context
    if ((!initialized && hasStoredData) || isLoading) {
      return INITIAL_LOADING_CONTEXT;
    }

    // Full context for authenticated pages
    return {
      user: userData,
      isAuth: !!userData,
      isLoading: isDecrypting || isLoading,
      isCheckingAuth: (!initialized && hasStoredData) || isLoading,
    };
  }, [
    userData,
    isLoading,
    initialized,
    hasStoredData,
    isDecrypting,
    isLoginPage,
    isPasswordResetPage,
  ]);

  // Log performance metrics
  useEffect(() => {
    markPerformance('auth-provider-render-complete');
    measurePerformance(
      'auth-provider-render-start',
      'auth-provider-render-complete',
      'auth-provider-time',
    );
  }, []);

  // Provide the auth context to children
  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

/**
 * Hook to access the authentication context
 * @returns The authentication context
 * @throws Error if used outside of AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
